import { useState, useEffect } from 'react';
import '../styles/epoch.css';

const EpochRewards = () => {
  const [epochData, setEpochData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredRewards, setFilteredRewards] = useState([]);

  useEffect(() => {
    fetchEpochData();
    const interval = setInterval(fetchEpochData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredRewards(rewards);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = rewards.filter(r => 
        r.wallet_address.toLowerCase().includes(query) ||
        r.node_id?.toLowerCase().includes(query)
      );
      setFilteredRewards(filtered);
    }
  }, [searchQuery, rewards]);

  const fetchEpochData = async () => {
    try {
      setLoading(true);
      const API_URL = import.meta.env.VITE_API_URL || 'https://node.canarymessenger.com';
      
      // Fetch current epoch info
      const epochRes = await fetch(`${API_URL}/api/rewards/current-epoch`);
      const epochInfo = await epochRes.json();
      
      // Fetch all rewards for current epoch
      const rewardsRes = await fetch(`${API_URL}/api/rewards/epoch/${epochInfo.current_epoch}`);
      const rewardsData = await rewardsRes.json();
      
      setEpochData(epochInfo);
      setRewards(rewardsData.rewards || []);
      setFilteredRewards(rewardsData.rewards || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCRED = (amount) => {
    return Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  if (loading && !epochData) {
    return (
      <div className="epoch-container">
        <div className="loading-spinner">Loading epoch data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="epoch-container">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="epoch-container">
      <header className="epoch-header">
        <h1>🏆 Canary Epoch Rewards</h1>
        <p className="subtitle">Real-time CRED distribution for node operators</p>
      </header>

      {epochData && (
        <div className="epoch-stats">
          <div className="stat-card">
            <div className="stat-label">Current Epoch</div>
            <div className="stat-value">{epochData.current_epoch}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Epoch Reward</div>
            <div className="stat-value">{formatCRED(epochData.base_reward)} CRED</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Distributed</div>
            <div className="stat-value">{formatCRED(epochData.total_distributed || 0)} CRED</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Nodes</div>
            <div className="stat-value">{rewards.length}</div>
          </div>
        </div>
      )}

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search by wallet address or node ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-btn" onClick={() => setSearchQuery('')}>
            Clear
          </button>
        )}
      </div>

      <div className="rewards-section">
        <h2>Epoch {epochData?.current_epoch} Rewards ({filteredRewards.length} addresses)</h2>
        
        {filteredRewards.length === 0 ? (
          <div className="no-results">
            {searchQuery ? 'No results found' : 'No rewards data available'}
          </div>
        ) : (
          <div className="rewards-table-container">
            <table className="rewards-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Wallet Address</th>
                  <th>Node ID</th>
                  <th>CRED Earned</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRewards.map((reward, idx) => (
                  <tr key={reward.id || idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <span className="wallet-address" title={reward.wallet_address}>
                        {formatAddress(reward.wallet_address)}
                      </span>
                    </td>
                    <td>
                      <span className="node-id" title={reward.node_id}>
                        {reward.node_id ? formatAddress(reward.node_id) : 'N/A'}
                      </span>
                    </td>
                    <td className="amount">{formatCRED(reward.amount)} CRED</td>
                    <td>
                      <span className={`status-badge status-${reward.status || 'pending'}`}>
                        {reward.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <footer className="epoch-footer">
        <p>Rewards update every 24 hours • Data refreshes every 30 seconds</p>
        <a href="/" className="back-link">← Back to Home</a>
      </footer>
    </div>
  );
};

export default EpochRewards;
